import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Skeleton,
  Alert,
  Tabs,
  Tab,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Chip,
  Stack,
} from '@mui/material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import BiotechIcon from '@mui/icons-material/Biotech';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { monitoramentoService } from '../../../services/api';

const NIVEIS = ['APS', 'AAE', 'AH'] as const;
type Nivel = typeof NIVEIS[number];

const NIVEL_LABEL: Record<Nivel, string> = {
  APS: 'Atencao Primaria (APS)',
  AAE: 'Atencao Ambulatorial Especializada (AAE)',
  AH: 'Atencao Hospitalar (AH)',
};

const NIVEL_COR: Record<Nivel, string> = {
  APS: '#388e3c',
  AAE: '#1976d2',
  AH: '#c62828',
};

const NIVEL_ICON: Record<Nivel, any> = {
  APS: LocalHospitalIcon,
  AAE: BiotechIcon,
  AH: MedicalServicesIcon,
};

interface ResumoItem {
  nivel_atencao: Nivel;
  total_procedimentos: number;
  categorias_distintas: number;
  valor_ambulatorial_total: number;
  categorias: string[];
}

interface ProcedimentoItem {
  codigo_procedimento: string;
  nivel_atencao: Nivel;
  enunciado_simplificado: string;
  categoria_simplificada: string;
  nome_procedimento_original: string;
  codgrupo: string;
  codsubgrupo: string;
  valambulatorial: number;
  total_registros: number;
}

interface Props {
  municipioId: number;
}

const DEBOUNCE_MS = 400;

const NiveisAtencaoSection: React.FC<Props> = ({ municipioId }) => {
  const [resumo, setResumo] = useState<ResumoItem[]>([]);
  const [procedimentos, setProcedimentos] = useState<ProcedimentoItem[]>([]);
  const [nivel, setNivel] = useState<Nivel>('APS');
  const [busca, setBusca] = useState('');
  const [buscaDebounced, setBuscaDebounced] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [loadingResumo, setLoadingResumo] = useState(true);
  const [loadingProc, setLoadingProc] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const buscaTimer = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingResumo(true);
    monitoramentoService.getParametrosResumoNiveis(municipioId)
      .then(r => { if (!cancelled) setResumo(r); })
      .catch(() => { if (!cancelled) setError('Falha ao carregar resumo de niveis'); })
      .finally(() => { if (!cancelled) setLoadingResumo(false); });
    return () => { cancelled = true; };
  }, [municipioId]);

  useEffect(() => {
    if (buscaTimer.current) clearTimeout(buscaTimer.current);
    buscaTimer.current = setTimeout(() => { setBuscaDebounced(busca); setPage(0); }, DEBOUNCE_MS);
    return () => { if (buscaTimer.current) clearTimeout(buscaTimer.current); };
  }, [busca]);

  useEffect(() => {
    let cancelled = false;
    setLoadingProc(true);
    monitoramentoService.getParametrosProcedimentosNivel(
      municipioId, nivel, buscaDebounced || undefined, page + 1, rowsPerPage,
    )
      .then(res => {
        if (cancelled) return;
        setProcedimentos(res);
        setTotal(res?.[0]?.total_registros || 0);
      })
      .catch(() => { if (!cancelled) setError('Falha ao carregar procedimentos'); })
      .finally(() => { if (!cancelled) setLoadingProc(false); });
    return () => { cancelled = true; };
  }, [municipioId, nivel, buscaDebounced, page, rowsPerPage]);

  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Box mb={2}>
        <Typography variant="subtitle1" fontWeight={600}>
          Niveis de Atencao e Procedimentos (RF-12.4)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Classificacao de procedimentos da tabela SUS por APS, AAE e AH com enunciado simplificado
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Cards resumo */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {NIVEIS.map(n => {
          const r = resumo.find(x => x.nivel_atencao === n);
          const Icon = NIVEL_ICON[n];
          return (
            <Grid item xs={12} sm={4} key={n}>
              {loadingResumo ? <Skeleton variant="rounded" height={100} /> : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderLeft: `4px solid ${NIVEL_COR[n]}`,
                    cursor: 'pointer',
                    bgcolor: nivel === n ? `${NIVEL_COR[n]}10` : 'inherit',
                  }}
                  onClick={() => { setNivel(n); setPage(0); }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <Icon sx={{ color: NIVEL_COR[n] }} />
                    <Typography variant="subtitle2" fontWeight={700}>{NIVEL_LABEL[n]}</Typography>
                  </Stack>
                  <Typography variant="h5" fontWeight={700}>
                    {Number(r?.total_procedimentos ?? 0).toLocaleString('pt-BR')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    procedimentos | {r?.categorias_distintas ?? 0} categorias
                  </Typography>
                </Paper>
              )}
            </Grid>
          );
        })}
      </Grid>

      {/* Tabs e busca */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
        <Tabs
          value={nivel}
          onChange={(_, v) => { setNivel(v); setPage(0); }}
          textColor="primary"
          indicatorColor="primary"
        >
          {NIVEIS.map(n => <Tab key={n} value={n} label={n} />)}
        </Tabs>
        <TextField
          size="small"
          label="Buscar (codigo ou enunciado)"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          sx={{ minWidth: 280 }}
        />
      </Stack>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Codigo</TableCell>
              <TableCell>Enunciado simplificado</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Nome original</TableCell>
              <TableCell align="right">Valor SUS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingProc && (
              <TableRow><TableCell colSpan={5}><Skeleton /></TableCell></TableRow>
            )}
            {!loadingProc && procedimentos.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    Nenhum procedimento encontrado.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {procedimentos.map(p => (
              <TableRow key={p.codigo_procedimento} hover>
                <TableCell><strong>{p.codigo_procedimento}</strong></TableCell>
                <TableCell>{p.enunciado_simplificado}</TableCell>
                <TableCell>
                  <Chip size="small" label={p.categoria_simplificada} variant="outlined" />
                </TableCell>
                <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.nome_procedimento_original}
                </TableCell>
                <TableCell align="right">
                  {p.valambulatorial != null
                    ? `R$ ${Number(p.valambulatorial).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 20, 50, 100]}
        labelRowsPerPage="Linhas por pagina"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />
    </Paper>
  );
};

export default NiveisAtencaoSection;
